import type {CancellationPolicy} from '@wix/ambassador-table-reservations-v1-reservation/types'

export type CancellationFeePreview =
  | {type: 'NO_POLICY'}
  | {type: 'FREE_CANCELLATION'; windowDescription: string}
  | {type: 'FEE_APPLIES'; totalAmount: string; currency: string}

const HOURS_IN_DAY = 24

function windowToHours(number: number, unit: string): number {
  return unit === 'DAYS' ? number * HOURS_IN_DAY : number
}

/**
 * Computes a fee preview for the guest at cancellation time, based on the reservation's
 * governing policy snapshot and current time relative to the reservation start.
 *
 * This is a client-side computation for UI purposes only.
 */
export function computeCancellationFeePreview(
  cancellationPolicy: CancellationPolicy | undefined | null,
  partySize: number | undefined | null,
  reservationStartDate: Date,
  currency: string | undefined | null,
  now: Date = new Date(),
): CancellationFeePreview {
  const fee = cancellationPolicy?.cancellationFee

  if (!fee) {
    return {type: 'NO_POLICY'}
  }

  const effectivePartySize = partySize ?? 0
  const minPartySize = fee.minPartySize ?? 1

  if (effectivePartySize < minPartySize) {
    return {type: 'NO_POLICY'}
  }

  const feeAmount = Number(fee.amount ?? 0)
  const totalFee = fee.unit === 'PER_GUEST' ? feeAmount * effectivePartySize : feeAmount
  const totalAmountStr = totalFee.toFixed(2)
  const usedCurrency = currency ?? ''

  const window = fee.freeCancellationWindow

  if (!window?.number || !window?.unit) {
    return {type: 'FEE_APPLIES', totalAmount: totalAmountStr, currency: usedCurrency}
  }

  const windowHours = windowToHours(window.number, window.unit as string)
  const windowDescription = window.unit === 'DAYS' ? `${window.number}d` : `${window.number}h`

  const hoursUntilStart = (reservationStartDate.getTime() - now.getTime()) / (1000 * 60 * 60)

  if (hoursUntilStart >= windowHours) {
    return {type: 'FREE_CANCELLATION', windowDescription}
  }

  return {type: 'FEE_APPLIES', totalAmount: totalAmountStr, currency: usedCurrency}
}
